<template>
  <div class="flex flex-col h-full relative">
    <!-- Close button -->
    <button @click="$emit('close')" class="absolute top-3 right-3 p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-200 z-10">
      <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
      </svg>
    </button>

    <!-- Top section -->
    <div class="px-4 pt-3 pb-1 space-y-1">
      <!-- Title -->
      <textarea
        v-model="form.title"
        @blur="saveField('title')"
        @keydown.enter.prevent="saveField('title')"
        @input="autoResizeTitle"
        ref="titleInput"
        rows="1"
        class="w-full pr-8 text-xl font-semibold resize-none border-none p-0 bg-transparent focus:outline-none focus:ring-0"
      ></textarea>

      <!-- ID + Tags + Type + Pillars -->
      <div class="flex items-center gap-1.5 flex-wrap text-xs">
        <span class="text-gray-400 font-mono cursor-pointer" @click="copyId" title="Copy ID">#{{ task.id }}</span>
        <span v-if="copied" class="text-green-500 text-[10px]">copied</span>
        <span class="text-gray-300">|</span>

        <span v-for="tag in (form.tags || [])" :key="tag"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-gray-100 dark:bg-gray-200 text-gray-600">
          {{ tag }}
          <button @click="removeTag(tag)" class="text-gray-400 hover:text-red-500 text-[10px]">&times;</button>
        </span>
        <button @click="showTagInput = !showTagInput" class="px-1.5 py-0.5 rounded border border-dashed border-gray-300 text-gray-400 hover:text-gray-500">+ Tag</button>
        <input v-if="showTagInput" ref="tagInputRef" v-model="newTag" @keydown.enter.prevent="addTag" @blur="showTagInput = false"
          placeholder="tag..." class="w-20 px-1.5 py-0.5 text-xs border border-gray-300 rounded focus:outline-none focus:border-teal-500" />

        <span class="text-gray-300">|</span>

        <button v-for="type in ['compound', 'cashflow', 'reactive']" :key="type"
          @click="setTaskType(type)"
          class="px-1.5 py-0.5 rounded border"
          :class="form.task_type === type
            ? 'bg-blue-50 border-blue-300 text-blue-700'
            : 'border-transparent text-gray-400 hover:text-gray-600'">
          {{ type }}
        </button>

        <span v-for="pillar in (form.pillars || [])" :key="pillar"
          class="inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-purple-50 text-purple-600 border border-purple-200">
          {{ pillar }}
          <button @click="togglePillar(pillar)" class="text-purple-400 hover:text-red-500 text-[10px]">&times;</button>
        </span>
        <div class="relative" ref="pillarDropdownRef">
          <button @click="showPillarDropdown = !showPillarDropdown"
            class="px-1.5 py-0.5 rounded border border-dashed border-purple-300 text-purple-400 hover:text-purple-500">+ Pillar</button>
          <div v-if="showPillarDropdown" class="absolute left-0 top-full mt-1 w-36 bg-white dark:bg-gray-50 border border-gray-200 rounded-md shadow-lg z-40 py-1">
            <button v-for="p in allPillars" :key="p" @click="togglePillar(p)"
              class="w-full text-left px-3 py-1 text-xs hover:bg-gray-100 flex items-center gap-2"
              :class="{ 'bg-purple-50 font-medium': (form.pillars || []).includes(p) }">
              <span class="w-3">{{ (form.pillars || []).includes(p) ? '✓' : '' }}</span>
              {{ p }}
            </button>
          </div>
        </div>
      </div>

      <!-- Date, Project, Section, Timer -->
      <div class="flex items-center gap-2 flex-wrap text-xs text-gray-500">
        <span>{{ formatDate(task.created_at) }}</span>
        <span class="text-gray-300">|</span>
        <select v-model="form.project_id" @change="saveField('project_id')"
          class="text-xs px-1 py-0.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-teal-500">
          <option :value="null">No project</option>
          <option v-for="p in sortedProjects" :key="p.id" :value="p.id">{{ p.display_label || p.name }}</option>
        </select>
        <select v-model="form.section_id" @change="saveField('section_id')"
          class="text-xs px-1 py-0.5 border border-gray-200 rounded bg-white focus:outline-none focus:border-teal-500">
          <option v-for="s in sections" :key="s.id" :value="s.id">{{ s.name }}</option>
        </select>
        <span class="text-gray-300">|</span>
        <template v-if="timeTracking.activeTaskId.value === task.id">
          <span
            class="w-2 h-2 rounded-full"
            :class="timeTracking.isPaused.value ? 'bg-amber-400' : 'bg-green-500 animate-pulse'"
            :title="timeTracking.isPaused.value ? 'Paused — window not focused or idle' : 'Tracking focus'"></span>
          <span
            class="font-mono"
            :class="timeTracking.isPaused.value ? 'text-amber-500' : 'text-green-600'">
            {{ timeTracking.isPaused.value ? 'Paused' : 'Focus' }}: {{ timeTracking.formatDuration(timeTracking.elapsed.value) }}
          </span>
        </template>
        <span v-if="task.total_time_seconds" class="text-gray-400">Total: {{ formatTotalTime }}</span>
      </div>
    </div>

    <!-- Description -->
    <div class="flex-1 px-4 overflow-y-auto">
      <TrixEditor
        v-model="form.description"
        @blur="saveField('description')"
        placeholder="Add description..."
      />
    </div>

    <!-- Bottom bar -->
    <div class="flex items-center justify-between px-4 py-2 border-t border-gray-200 dark:border-gray-300">
      <div class="flex items-center gap-3">
        <input v-model="form.context" @blur="saveField('context')" placeholder="@context..."
          class="text-xs px-2 py-1 border border-gray-200 rounded bg-white focus:outline-none focus:border-teal-500 w-32" />
        <span v-if="task.classified_by" class="text-[10px] text-gray-400">by {{ task.classified_by }}</span>
      </div>
      <div class="flex items-center gap-3">
        <span v-if="saved" class="text-xs text-green-500">Saved!</span>
        <button @click="confirmDelete" class="text-xs text-red-400 hover:text-red-600">Delete</button>
        <button @click="saveAll" class="px-3 py-1 text-xs font-medium bg-teal-500 text-white rounded hover:bg-teal-600">Save</button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, watch, computed, nextTick, onMounted, onUnmounted } from 'vue'
import TrixEditor from '../shared/TrixEditor.vue'

const props = defineProps({
  task: Object,
  projects: Array,
  sections: Array,
  timeTracking: Object,
})

const emit = defineEmits(['update', 'delete', 'close', 'start-session', 'stop-session'])

const allPillars = ['systems', 'sales', 'team', 'network', 'brand', 'strategy', 'health', 'resilience']

const form = reactive({
  title: '', description: '', project_id: null, section_id: null,
  task_type: null, pillars: [], tags: [], context: '',
})

const newTag = ref('')
const showTagInput = ref(false)
const showPillarDropdown = ref(false)
const copied = ref(false)
const saved = ref(false)
let savedTimeout = null
const tagInputRef = ref(null)
const titleInput = ref(null)
const pillarDropdownRef = ref(null)

watch(() => props.task, (t, oldT) => {
  if (t) {
    form.title = t.title || ''
    form.description = t.description || ''
    form.project_id = t.project_id || null
    form.section_id = t.section_id || null
    form.task_type = t.task_type || null
    form.pillars = [...(t.pillars || [])]
    form.tags = [...(t.tags || [])]
    form.context = t.context || ''
    nextTick(autoResizeTitle)

    if (t.id !== oldT?.id && props.timeTracking.activeTaskId.value !== t.id) {
      emit('start-session', t.id)
    }
  }
}, { immediate: true })

watch(showTagInput, (v) => {
  if (v) nextTick(() => tagInputRef.value?.focus())
})

const sortedProjects = computed(() =>
  [...(props.projects || [])].sort((a, b) =>
    (a.display_label || a.name).localeCompare(b.display_label || b.name)
  )
)

const formatTotalTime = computed(() => {
  const s = props.task.total_time_seconds || 0
  if (s === 0) return ''
  const h = Math.floor(s / 3600)
  const m = Math.floor((s % 3600) / 60)
  return h > 0 ? `${h}h ${m}m` : `${m}m`
})

function formatDate(d) {
  if (!d) return ''
  return new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
}

function autoResizeTitle() {
  if (titleInput.value) {
    titleInput.value.style.height = 'auto'
    titleInput.value.style.height = titleInput.value.scrollHeight + 'px'
  }
}

function copyId() {
  navigator.clipboard.writeText(String(props.task.id))
  copied.value = true
  setTimeout(() => copied.value = false, 1500)
}

function showSaved() {
  saved.value = true
  clearTimeout(savedTimeout)
  savedTimeout = setTimeout(() => saved.value = false, 1500)
}

function saveField(field) {
  emit('update', props.task.id, { [field]: form[field] })
  showSaved()
}

function setTaskType(type) {
  form.task_type = form.task_type === type ? null : type
  saveField('task_type')
}

function togglePillar(pillar) {
  const idx = form.pillars.indexOf(pillar)
  if (idx === -1) form.pillars.push(pillar)
  else form.pillars.splice(idx, 1)
  saveField('pillars')
}

function addTag() {
  const tag = newTag.value.trim()
  if (tag && !form.tags.includes(tag)) { form.tags.push(tag); saveField('tags') }
  newTag.value = ''
  showTagInput.value = false
}

function removeTag(tag) { form.tags = form.tags.filter(t => t !== tag); saveField('tags') }

function saveAll() {
  emit('update', props.task.id, {
    title: form.title, description: form.description, project_id: form.project_id,
    section_id: form.section_id, task_type: form.task_type, pillars: form.pillars,
    tags: form.tags, context: form.context,
  })
  showSaved()
}

function confirmDelete() { if (confirm('Delete this task?')) emit('delete', props.task.id) }

function handleClickOutside(e) {
  if (pillarDropdownRef.value && !pillarDropdownRef.value.contains(e.target)) showPillarDropdown.value = false
}

onMounted(() => document.addEventListener('click', handleClickOutside))
onUnmounted(() => document.removeEventListener('click', handleClickOutside))
</script>
